<template>
  <div>
    <button
      style="display: none"
      type="button"
      class="btn-show-modal-promo-code"
      data-bs-toggle="modal"
      data-bs-target="#modalPromoCode"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-modal-promo-code"
      data-bs-dismiss="modal"
      data-bs-target="#modalPromoCode"
    ></button>
    <div
      class="modal fade"
      id="modalPromoCode"
      tabindex="-1"
      aria-labelledby="modalPromoCodeLabel"
      aria-hidden="true"
    >
      <KskModalShell :width="780" :show-close="false">
        <div class="ksk-promo">
          <h2 id="modalPromoCodeLabel" class="ksk-promo__title">{{ $t("kiosk.promo.title") }}</h2>

          <div class="ksk-promo__field" :class="{ 'ksk-promo__field--error': errorMessage }">
            <input
              ref="promoCodeInput"
              type="text"
              class="ksk-promo__input"
              v-model="promoCode"
              :placeholder="$t('kiosk.promo.placeholder')"
              @keyup.enter="applyPromoCode"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              maxlength="50"
            />
            <button
              v-if="promoCode"
              type="button"
              class="ksk-promo__clear"
              :aria-label="$t('kiosk.promo.clearAria')"
              @click="clearCode"
            >
              <span class="material-icons-round">close</span>
            </button>
          </div>

          <!-- Reserved error line: always takes its height so the keyboard never jumps. -->
          <div class="ksk-promo__error" role="alert">
            <template v-if="errorMessage">
              <span class="material-icons-round">error_outline</span>
              <span>{{ errorMessage }}</span>
            </template>
          </div>

          <div class="ksk-promo__keyboard">
            <div class="ksk-promo__row">
              <button
                v-for="key in displayRow1"
                :key="key"
                type="button"
                class="ksk-promo__key"
                @click="addCharacter(key)"
              >{{ key }}</button>
            </div>
            <div class="ksk-promo__row">
              <button
                v-for="key in displayRow2"
                :key="key"
                type="button"
                class="ksk-promo__key"
                @click="addCharacter(key)"
              >{{ key }}</button>
            </div>
            <div class="ksk-promo__row ksk-promo__row--inset">
              <button
                v-for="key in displayRow3"
                :key="key"
                type="button"
                class="ksk-promo__key"
                @click="addCharacter(key)"
              >{{ key }}</button>
            </div>
            <div class="ksk-promo__row">
              <button
                type="button"
                class="ksk-promo__key ksk-promo__key--action ksk-promo__key--wide"
                :class="{ 'ksk-promo__key--on': isShiftPressed }"
                :aria-label="$t('kiosk.promo.shiftAria')"
                @click="toggleShift"
              >
                <span class="material-icons-round">arrow_upward</span>
              </button>
              <button
                v-for="key in displayRow4"
                :key="key"
                type="button"
                class="ksk-promo__key"
                @click="addCharacter(key)"
              >{{ key }}</button>
              <button
                type="button"
                class="ksk-promo__key ksk-promo__key--action ksk-promo__key--wide"
                :aria-label="$t('kiosk.promo.deleteAria')"
                @click="deleteCharacter"
              >
                <span class="material-icons-round">backspace</span>
              </button>
            </div>
            <!-- No separate Clear row: the ✕ in the code field clears it. -->
          </div>
        </div>

        <template #footer>
          <KskButton class="ksk-promo__cancel" variant="outlined" size="md" @click="cancelPromoCode">{{ $t("kiosk.common.cancel") }}</KskButton>
          <KskButton
            class="ksk-promo__apply"
            size="md"
            :disabled="!promoCode.trim() || isApplying"
            @click="applyPromoCode"
          >{{ $t("kiosk.promo.apply") }}</KskButton>
        </template>
      </KskModalShell>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import KskButton from "./ui/KskButton.vue";
import KskModalShell from "./ui/KskModalShell.vue";

export default {
  name: "ModalPromoCode",
  emits: ["apply-promo-code"],
  components: {
    KskButton,
    KskModalShell,
  },
  data() {
    return {
      promoCode: "",
      isShiftPressed: true,
      // KSK027: the popup stays open while Checkout checks the code; Checkout
      // reports back through applySucceeded() / applyFailed().
      isApplying: false,
      errorMessage: "",
      row1: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
      row2: ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
      row3: ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
      row4: ["Z", "X", "C", "V", "B", "N", "M"],
    };
  },
  mounted() {
    // Listen for modal show event
    const modal = document.getElementById('modalPromoCode');
    if (modal) {
      modal.addEventListener('shown.bs.modal', this.focusInput);
      modal.addEventListener('hidden.bs.modal', this.clearStatus);
      // KSK065: a scanner types like a keyboard — keep its keys in the field.
      modal.addEventListener('shown.bs.modal', this.listenKeys);
      modal.addEventListener('hidden.bs.modal', this.unlistenKeys);
    }
  },
  beforeUnmount() {
    // Clean up event listener
    const modal = document.getElementById('modalPromoCode');
    if (modal) {
      modal.removeEventListener('shown.bs.modal', this.focusInput);
      modal.removeEventListener('hidden.bs.modal', this.clearStatus);
      modal.removeEventListener('shown.bs.modal', this.listenKeys);
      modal.removeEventListener('hidden.bs.modal', this.unlistenKeys);
    }
    this.unlistenKeys();
  },
  watch: {
    promoCode() {
      this.errorMessage = "";
    },
  },
  computed: {
    displayRow1() {
      return this.row1;
    },
    displayRow2() {
      return this.isShiftPressed ? this.row2 : this.row2.map(letter => letter.toLowerCase());
    },
    displayRow3() {
      return this.isShiftPressed ? this.row3 : this.row3.map(letter => letter.toLowerCase());
    },
    displayRow4() {
      return this.isShiftPressed ? this.row4 : this.row4.map(letter => letter.toLowerCase());
    }
  },
  methods: {
    focusInput() {
      // Focus the input field when modal is shown
      this.$nextTick(() => {
        if (this.$refs.promoCodeInput) {
          this.$refs.promoCodeInput.focus();
        }
      });
    },
    listenKeys() {
      window.addEventListener('keydown', this.handleGlobalKeydown);
    },
    unlistenKeys() {
      window.removeEventListener('keydown', this.handleGlobalKeydown);
    },
    // A key typed while an on-screen key holds focus goes to the field instead.
    handleGlobalKeydown(e) {
      const input = this.$refs.promoCodeInput;
      if (!input || document.activeElement === input) return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        this.promoCode += e.key;
        this.focusInput();
      } else if (e.key === 'Enter' && !e.repeat) {
        e.preventDefault();
        this.applyPromoCode();
      }
    },
    clearStatus() {
      this.errorMessage = "";
      this.isApplying = false;
    },
    addCharacter(char) {
      this.promoCode += char;
    },
    deleteCharacter() {
      this.promoCode = this.promoCode.slice(0, -1);
      if (this.promoCode.length === 0) {
        this.focusInput();
      }
    },
    clearCode() {
      this.promoCode = "";
      this.focusInput();
    },
    toggleShift() {
      this.isShiftPressed = !this.isShiftPressed;
    },
    cancelPromoCode() {
      this.promoCode = "";
      this.isShiftPressed = true; // Reset về chế độ uppercase
      $(".btn-hide-modal-promo-code").click();
    },
    applyPromoCode() {
      if (this.promoCode.trim() && !this.isApplying) {
        this.errorMessage = "";
        this.isApplying = true;
        this.$emit("apply-promo-code", this.promoCode.trim());
      }
    },
    applySucceeded() {
      this.promoCode = "";
      this.isApplying = false;
      $(".btn-hide-modal-promo-code").click();
    },
    applyFailed(message) {
      this.isApplying = false;
      // KSK027: shown when the API gives no reason of its own.
      this.errorMessage = message || this.$t("kiosk.promo.invalid");
    },
  },
};
</script>

<style scoped>
/* Sized to the kiosk dialogs: 780px (room for a 10-key row), 40px padding,
   32px title, 64px keys, 80px buttons. */
.modal :deep(.ksk-modal) {
  padding: 40px;
}
.modal :deep(.ksk-modal__footer) {
  margin-top: var(--ksk-space-3);
}
.ksk-promo {
  display: flex;
  flex-direction: column;
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}
.ksk-promo__title {
  margin: 0 0 var(--ksk-space-3);
  font-family: var(--ksk-font);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ksk-text);
}

.ksk-promo__field {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  height: 72px;
  padding: 0 12px 0 var(--ksk-space-3);
  border: 2px solid transparent;
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface-muted);
}
.ksk-promo__field:focus-within {
  border-color: var(--ksk-primary);
}
.ksk-promo__field--error,
.ksk-promo__field--error:focus-within {
  border-color: var(--ksk-error);
}
/* The code lookup is a plain MySQL `where` on a _ci column, so showing the
   code in capitals never misrepresents what is sent. */
.ksk-promo__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--ksk-font);
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ksk-text);
}
.ksk-promo__input::placeholder {
  font-size: 22px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ksk-text-secondary);
}
.ksk-promo__clear {
  width: 48px;
  height: 48px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--ksk-surface);
  color: var(--ksk-text);
  cursor: pointer;
}
.ksk-promo__clear .material-icons-round {
  font-size: 26px;
}

.ksk-promo__error {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-1);
  min-height: 36px;
  padding: 6px var(--ksk-space-1) 0;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 600;
  color: var(--ksk-error);
}
.ksk-promo__error .material-icons-round {
  font-size: 24px;
}

.ksk-promo__keyboard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: var(--ksk-space-1);
}
.ksk-promo__row {
  display: flex;
  gap: 6px;
}
/* 9 keys line up under the 10-key rows, half a key in from each side. */
.ksk-promo__row--inset {
  padding: 0 calc((100% - 9 * 6px) / 20);
}
.ksk-promo__key {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ksk-space-1);
  /* KSK063: muted key tiles, no outline (same as the numeric keypad). */
  border: 1px solid transparent;
  border-radius: 14px;
  background: var(--ksk-surface-muted);
  color: var(--ksk-text);
  font-family: var(--ksk-font);
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  transition: transform 80ms ease-out;
  -webkit-tap-highlight-color: transparent;
}
.ksk-promo__key .material-icons-round {
  font-size: 28px;
}
.ksk-promo__key::after {
  content: "";
  position: absolute;
  inset: 0;
  background: currentColor;
  opacity: 0;
}
.ksk-promo__key:active {
  transform: scale(0.97);
}
.ksk-promo__key:active::after {
  opacity: 0.08;
}
.ksk-promo__key--wide {
  flex-grow: 1.5;
}
.ksk-promo__key--action {
  background: var(--ksk-surface-muted);
  border-color: transparent;
}
.ksk-promo__key--on {
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
}

/* The shell's `footer > * { flex: 1 }` is scoped to the shell, so it never
   reaches slotted buttons — size them here. */
.ksk-promo__cancel {
  flex: 0 0 220px;
}
.ksk-promo__apply {
  flex: 1;
}
</style>
